import { useState } from "react";

import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { type HandoffSeverity, listPendingHandoffs } from "@/api/handoffs";

import { HandoffDrawer } from "./HandoffDrawer";

function severityColor(s: HandoffSeverity): "default" | "warning" | "error" {
  if (s === "WARNING") return "warning";
  if (s === "CRITICAL") return "error";
  return "default";
}

export function PendingHandoffsCard() {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);

  const pending = useQuery({
    queryKey: ["pending-handoffs"],
    queryFn: listPendingHandoffs,
    staleTime: 60_000,
  });

  const items = pending.data ?? [];
  if (pending.isLoading || items.length === 0) return null;

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <AssignmentTurnedInIcon color="primary" fontSize="small" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t("handoff.pendingTitle")}
            </Typography>
            <Chip size="small" color="primary" label={items.length} />
          </Stack>
          <Stack spacing={1}>
            {items.map((p) => (
              <Paper
                key={p.handoff.id}
                variant="outlined"
                sx={{ p: 1.25 }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.25}
                  alignItems={{ sm: "center" }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Chip
                        size="small"
                        color={severityColor(p.handoff.severity)}
                        label={t(`handoff.severity.${p.handoff.severity}`)}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                      >
                        {p.outgoing_date}
                      </Typography>
                      <Typography variant="body2">— {p.outgoing_slot_label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.organization_name}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {p.handoff.body}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setOpenId(p.handoff.assignment_id)}
                  >
                    {t("handoff.readCta")}
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </CardContent>
      </Card>
      <HandoffDrawer
        open={Boolean(openId)}
        assignmentId={openId}
        canAuthor={false}
        onClose={() => setOpenId(null)}
      />
    </>
  );
}
